import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2 } from 'lucide-react';
import { useThresholds, useUpsertThreshold, locationHooks } from '../../hooks/useMasters.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../utils/api.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Select from '../../components/ui/Select.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { GP_TYPE_LABELS } from '../../utils/constants.js';

const schema = z.object({
  locationId: z.string().min(1, 'Required'),
  gpType: z.string().min(1, 'Required'),
  threshold: z.coerce.number().min(0, 'Must be positive'),
});

export default function ApprovalThresholdsPage() {
  const { data: thresholds = [], isLoading } = useThresholds();
  const { data: locations = [] } = locationHooks.useList();
  const upsert = useUpsertThreshold();
  const qc = useQueryClient();
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/masters/approval-thresholds/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['approval-thresholds'] }),
  });
  const [modalOpen, setModalOpen] = useState(false);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (values) => {
    await upsert.mutateAsync(values);
    setModalOpen(false); reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Approval Thresholds</h2>
          <p className="text-sm text-gray-500">Quantity (kg) above which a second Admin approval is required</p>
        </div>
        <Button icon={Plus} onClick={() => { reset({}); setModalOpen(true); }}>Add Threshold</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Location','Gate Pass Type','Threshold (kg)',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {thresholds.map(t => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{t.location?.name}</td>
                    <td className="px-4 py-3 text-gray-600">{GP_TYPE_LABELS[t.gpType] || t.gpType}</td>
                    <td className="px-4 py-3 font-semibold">{t.threshold.toLocaleString()} kg</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => deleteMut.mutate(t.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add Approval Threshold">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Select label="Location" required error={errors.locationId?.message} {...register('locationId')}>
            <option value="">Select location…</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.code} — {l.name}</option>)}
          </Select>
          <Select label="Gate Pass Type" required error={errors.gpType?.message} {...register('gpType')}>
            <option value="">Select type…</option>
            {Object.entries(GP_TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <Input label="Threshold (kg)" type="number" step="0.01" required error={errors.threshold?.message} {...register('threshold')} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
